import axios from 'axios';
import { getCurrentUser } from './firebase';
import { devLog } from '../utils/devLog';

// Use VITE_API_TARGET from environment, fallback to localhost
const API_TARGET = import.meta.env.VITE_API_TARGET || 'http://localhost:8000';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: `${API_TARGET}/api/v1`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add CSRF token
api.interceptors.request.use(
  (config) => {
    // Get CSRF token from meta tag
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (token) {
      config.headers['X-CSRF-TOKEN'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Helper function to get CSRF token
async function getCSRFToken() {
  // Try to get from meta tag first
  const metaToken = document.querySelector('meta[name="csrf-token"]')?.content;
  if (metaToken) return metaToken;
  
  // Fallback: try to get from cookie
  const cookies = document.cookie.split(';');
  const csrfCookie = cookies.find(cookie => cookie.trim().startsWith('XSRF-TOKEN='));
  if (csrfCookie) {
    return decodeURIComponent(csrfCookie.split('=')[1]);
  }
  
  // If no token found, try to get a fresh one from the server
  try {
    const response = await fetch('/api/csrf-token', { 
      method: 'GET',
      credentials: 'include'
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.token;
    }
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
  }
  
  return '';
}

// Helper function to get user ID
const getUserId = () => {
  const user = getCurrentUser();
  if (user && user.uid) {
    return user.uid;
  }
  return 'guest_' + Math.random().toString(36).substring(2, 15);
};

// Search API functions
export const searchAPI = {
  // Web search
  fetchWebResults: async (query, page = 1) => {
    try {
      const response = await api.get(`/search/videos?q=${encodeURIComponent(query)}&pageno=${page}`);
      return response.data;
    } catch (error) {
      console.error('Web search error:', error);
      // Return mock data if backend is not available
      return {
        results: [
          {
            title: 'Sample Web Result',
            link: 'https://example.com',
            snippet: 'This is a sample search result. The backend might not be running.'
          }
        ]
      };
    }
  },

  // Game search
  fetchGameResults: async (query, page = 1) => {
    try {
      const response = await api.get(`/search/game?q=${encodeURIComponent(query)}&pageno=${page}`);
      return response.data;
    } catch (error) {
      console.error('Game search error:', error);
      return {
        results: [
          {
            title: 'Sample Game Result',
            link: 'https://example.com/game',
            snippet: 'This is a sample game search result.'
          }
        ]
      };
    }
  },

  // Education search
  fetchEducationResults: async (query, page = 1) => {
    try {
      const response = await api.get(`/search/education?q=${encodeURIComponent(query)}&pageno=${page}`);
      return response.data;
    } catch (error) {
      console.error('Education search error:', error);
      return {
        results: [
          {
            title: 'Sample Education Result',
            link: 'https://example.com/education',
            snippet: 'This is a sample education search result.'
          }
        ]
      };
    }
  },

  // Facts search
  fetchFactsResults: async (query, page = 1) => {
    try {
      const response = await api.get(`/search/facts?q=${encodeURIComponent(query)}&pageno=${page}`);
      return response.data;
    } catch (error) {
      console.error('Facts search error:', error);
      return {
        results: [
          {
            title: 'Sample Facts Result',
            link: 'https://example.com/facts',
            snippet: 'This is a sample facts search result.'
          }
        ]
      };
    }
  },

  // News search
  fetchNewsResults: async (query, page = 1) => {
    try {
      const response = await api.get(`/search/news?q=${encodeURIComponent(query)}&pageno=${page}`);
      return response.data;
    } catch (error) {
      console.error('News search error:', error);
      return {
        results: [
          {
            title: 'Sample News Result',
            link: 'https://example.com/news',
            snippet: 'This is a sample news search result.'
          }
        ]
      };
    }
  },

  // Images search (placeholder - implement based on your needs)
  fetchImageResults: async (query, page = 1) => {
    try {
      const response = await api.get(`/search/images?q=${encodeURIComponent(query)}&pageno=${page}`);
      return response.data;
    } catch (error) {
      console.error('Image search error:', error);
      return {
        results: [
          {
            title: 'Sample Image Result',
            link: 'https://example.com/image',
            snippet: 'This is a sample image search result.',
            image: 'https://via.placeholder.com/300x200?text=Sample+Image'
          }
        ]
      };
    }
  },

  // Videos search (same as web for now)
  fetchVideoResults: async (query, page = 1) => {
    try {
      const response = await api.get(`/search/videos?q=${encodeURIComponent(query)}&pageno=${page}`);
      return response.data;
    } catch (error) {
      console.error('Video search error:', error);
      return {
        results: [
          {
            title: 'Sample Video Result',
            link: 'https://example.com/video',
            snippet: 'This is a sample video search result.'
          }
        ]
      };
    }
  },
};

// AI Overview API
export const aiAPI = {
  fetchOverview: async (query, userId = null, sessionId = null) => {
    try {
      const data = { q: query };
      if (userId) data.user_id = userId;
      if (sessionId) data.session_id = sessionId;

      const response = await api.post('/ai/overview', data);
      devLog('AI Overview response:', response.data);
      return response.data;
    } catch (error) {
      console.error('AI Overview error:', error);
      throw error;
    }
  },
};

// Chat API
export const chatAPI = {
  sendMessage: async (message, files = [], conversationId = null, userId = null) => {
    const requestBody = {
      body: JSON.stringify({
        action: "sendMessage",
        userId: userId || getUserId(),
        conversationId: conversationId,
        message: message,
        files: files
      })
    };
    
    try {
      const response = await api.post('/chat', requestBody);
      return response.data;
    } catch (error) {
      console.error('Chat error:', error);
      return {
        message: 'Sorry, the chat service is currently unavailable. Please try again later.',
        status: 'offline'
      };
    }
  },
};

// Topics API
export const topicsAPI = {
  fetchTopics: async (topic = 'Nature') => {
    try {
      const response = await api.get(`/topics?topic=${encodeURIComponent(topic)}`);
      return response.data;
    } catch (error) {
      console.error('Topics error:', error);
      return {
        title: 'Sample Topic',
        content: {
          '4-6': { text: 'Sample content for ages 4-6' },
          '7-9': { text: 'Sample content for ages 7-9' },
          '10-12': { text: 'Sample content for ages 10-12' },
          '13-14': { text: 'Sample content for ages 13-14' }
        },
        children: []
      };
    }
  },

  fetchTopicsList: async () => {
    try {
      const response = await api.get('/topics/list');
      return response.data;
    } catch (error) {
      console.error('Topics list error:', error);
      return {
        topics: ['Nature', 'Geography', 'Science', 'Space', 'Animals', 'History', 'Art', 'Music']
      };
    }
  },

  fetchNode: async (nodeId) => {
    try {
      const response = await api.get(`/topics?topic=${encodeURIComponent(nodeId)}`);
      return response.data;
    } catch (error) {
      console.error('Node fetch error:', error);
      return {
        title: 'Sample Node',
        content: {
          '4-6': { text: 'Sample node content for ages 4-6' },
          '7-9': { text: 'Sample node content for ages 7-9' },
          '10-12': { text: 'Sample node content for ages 10-12' },
          '13-14': { text: 'Sample node content for ages 13-14' }
        },
        children: []
      };
    }
  },

  fetchChildren: async (nodeId) => {
    try {
      const response = await api.get(`/topics?topic=${encodeURIComponent(nodeId)}`);
      return response.data;
    } catch (error) {
      console.error('Children fetch error:', error);
      return {
        children: []
      };
    }
  },
};

// File upload API
export const uploadAPI = {
  getUploadUrl: async (contentType, userId) => {
    try {
      devLog('UploadAPI: Requesting upload URL for:', contentType, userId);

      const requestBody = {
        body: JSON.stringify({
          contentType: contentType,
          userId: userId || getUserId()
        })
      };

      const response = await api.post('/upload-url', requestBody);
      devLog('UploadAPI: Response status:', response.status, 'data keys:', Object.keys(response.data || {}));

      // Check if response.data is a string that needs parsing
      if (typeof response.data === 'string') {
        try {
          const parsed = JSON.parse(response.data);
          devLog('UploadAPI: Parsed string response');
          return parsed;
        } catch (parseError) {
          console.error('UploadAPI: Failed to parse string response:', parseError);
          return response.data;
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Upload URL error:', error);
      console.error('Upload URL error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers
      });
      return {
        error: 'Upload service unavailable',
        status: 'offline'
      };
    }
  },
};

// User authentication API
export const authAPI = {
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      return { status: '0', message: 'Registration failed' };
    }
  },

  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      return { status: '0', message: 'Login failed' };
    }
  },

  socialLogin: async (provider) => {
    try {
      const response = await api.post('/auth/social-login', { provider });
      return response.data;
    } catch (error) {
      console.error('Social login error:', error);
      return { status: '0', message: 'Social login failed' };
    }
  },

  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Forgot password error:', error);
      return { status: '0', message: 'Password reset failed' };
    }
  },

  resetPassword: async (token, password) => {
    try {
      const response = await api.post('/auth/reset-password', { token, password });
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      return { status: '0', message: 'Password reset failed' };
    }
  },

  updateUser: async (userData) => {
    try {
      const response = await api.post('/auth/update-user', userData);
      return response.data;
    } catch (error) {
      console.error('Update user error:', error);
      return { status: '0', message: 'Update failed' };
    }
  },

  logout: async () => {
    try {
      const response = await api.post('/auth/logout');
      return response.data;
    } catch (error) {
      console.error('Logout error:', error);
      return { status: '1', message: 'Logged out' };
    }
  },
};

// Error logging API
export const errorAPI = {
  logError: async (errorData) => {
    try {
      const response = await api.post('/log-error', errorData);
      return response.data;
    } catch (error) {
      console.error('Error logging failed:', error);
      return { status: '0', message: 'Error logging failed' };
    }
  },
};

export default api; 